// Speechmatics realtime provider (default, confirmed in Phase-0). Mints a
// short-lived JWT from the management API (the only CORS-sensitive step), opens
// the realtime socket with `?jwt=`, and normalizes AddPartialTranscript /
// AddTranscript messages into ProviderResult batches. Speaker label lives at
// results[].alternatives[0].speaker ('S1'); timing at results[].start_time/
// end_time (seconds).

import type {
  ConnectOptions,
  ConnState,
  MintedToken,
  ProviderError,
  ProviderResult,
  ProviderToken,
  TranscriptionProvider,
  TranscriptionSession
} from '../types'

const MP_URL = 'https://mp.speechmatics.com/v1/api_keys?type=rt'
const TOKEN_TTL_S = 3600

interface SmAlternative {
  content: string
  speaker?: string
  confidence?: number
}
interface SmResult {
  type?: string
  start_time?: number
  end_time?: number
  alternatives?: SmAlternative[]
}
interface SmMessage {
  message: string
  reason?: string
  type?: string
  results?: SmResult[]
}

function toTokens(results: SmResult[] | undefined, isFinal: boolean): ProviderToken[] {
  const tokens: ProviderToken[] = []
  for (const r of results ?? []) {
    const alt = r.alternatives?.[0]
    if (!alt) continue
    tokens.push({
      text: alt.content,
      providerSpeaker: alt.speaker ?? '?',
      isFinal,
      startMs: r.start_time !== undefined ? Math.round(r.start_time * 1000) : undefined,
      endMs: r.end_time !== undefined ? Math.round(r.end_time * 1000) : undefined,
      confidence: alt.confidence
    })
  }
  return tokens
}

function classifyError(type: string | undefined, reason: string | undefined): ProviderError {
  const t = (type ?? '').toLowerCase()
  if (t.includes('auth') || t.includes('forbidden')) {
    return { kind: 'auth', message: reason ?? type ?? 'auth error', retryable: false }
  }
  if (t.includes('quota') || t.includes('limit')) {
    return { kind: 'quota', message: reason ?? type ?? 'quota exceeded', retryable: false }
  }
  if (t.includes('format') || t.includes('audio')) {
    return { kind: 'format', message: reason ?? type ?? 'format error', retryable: false }
  }
  return { kind: 'unknown', message: reason ?? type ?? 'error', retryable: true }
}

export function createSpeechmaticsProvider(): TranscriptionProvider {
  return {
    id: 'speechmatics',

    async mintToken(apiKey: string): Promise<MintedToken> {
      const res = await fetch(MP_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({ ttl: TOKEN_TTL_S })
      })
      if (!res.ok) {
        throw new Error(`token mint failed: HTTP ${res.status} ${await res.text()}`)
      }
      const json = (await res.json()) as { key_value?: string }
      if (!json.key_value) throw new Error('token mint: no key_value in response')
      return { token: json.key_value, expiresAt: Date.now() + TOKEN_TTL_S * 1000 }
    },

    connect(opts: ConnectOptions): TranscriptionSession {
      const region = opts.region ?? 'eu'
      const ws = new WebSocket(
        `wss://${region}.rt.speechmatics.com/v2?jwt=${opts.token}`
      )
      ws.binaryType = 'arraybuffer'

      let ready = false
      let seqNo = 0
      const queue: ArrayBuffer[] = []
      let onResult: (r: ProviderResult) => void = () => {}
      let onState: (s: ConnState, d?: string) => void = () => {}
      let onError: (e: ProviderError) => void = () => {}

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            message: 'StartRecognition',
            audio_format: {
              type: 'raw',
              encoding: 'pcm_s16le',
              sample_rate: opts.sampleRate
            },
            transcription_config: {
              language: 'en',
              diarization: 'speaker',
              max_delay: 1.0,
              enable_partials: true,
              ...(opts.maxSpeakers
                ? { speaker_diarization_config: { max_speakers: opts.maxSpeakers } }
                : {})
            }
          })
        )
      }

      ws.onmessage = (e: MessageEvent<string>) => {
        const msg = JSON.parse(e.data) as SmMessage
        switch (msg.message) {
          case 'RecognitionStarted':
            ready = true
            onState('live', 'recognition started')
            for (const buf of queue) {
              ws.send(buf)
              seqNo++
            }
            queue.length = 0
            break
          case 'AddPartialTranscript':
            onResult({ tokens: toTokens(msg.results, false), isFinal: false })
            break
          case 'AddTranscript':
            onResult({ tokens: toTokens(msg.results, true), isFinal: true })
            break
          case 'EndOfTranscript':
            onState('closed', 'end of transcript')
            ws.close()
            break
          case 'Error':
            onError(classifyError(msg.type, msg.reason))
            onState('error', `${msg.type ?? 'error'}: ${msg.reason ?? ''}`)
            break
          default:
            break
        }
      }

      ws.onerror = () => onError({ kind: 'network', message: 'websocket error', retryable: true })
      ws.onclose = (e) => {
        if (ready) onState('closed', `socket closed (${e.code})`)
        else onState('error', `closed before start (${e.code}) — check JWT/region`)
      }

      return {
        sendAudio(pcm) {
          if (!ready) {
            queue.push(pcm)
            return
          }
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(pcm)
            seqNo++
          }
        },
        onResult: (cb) => {
          onResult = cb
        },
        onState: (cb) => {
          onState = cb
        },
        onError: (cb) => {
          onError = cb
        },
        close() {
          return new Promise<void>((resolve) => {
            if (ws.readyState === WebSocket.OPEN) {
              onState('closing', 'sending EndOfStream')
              ws.send(JSON.stringify({ message: 'EndOfStream', last_seq_no: seqNo }))
            }
            ws.onclose = () => resolve()
            ws.close()
          })
        }
      }
    }
  }
}
